/**
 * share-dialog.tsx — Option A modal for the first-class shares model
 * (spec: [[project-shares-model]] §9). Sole share UI as of Task 14 cleanup;
 * replaced the pre-0013 per-asset dialogs that lived under projects/ + watch/.
 *
 * One dialog serves all five entry points (spec):
 *   1. Asset three-dot menu   → { items: [{type: 'video', id}] }
 *   2. Folder three-dot menu  → { items: [{type: 'folder', id}] }
 *   3. Watch page Share button (wired 2026-07-18, Task 14 cleanup)
 *   4. Master Share on project detail  → items from the caller's selection
 *   5. Master Share on folder detail   → items from the caller's selection
 *
 * The component is intentionally stateless about which entry it opened from —
 * caller passes a resolved `items` array. Caller also owns open/close state.
 *
 * Two panels:
 *   BEFORE CREATE — settings form + Create button.
 *   AFTER CREATE  — /s/:token URL + Copy + Rotate + Revoke, and settings that
 *                   the owner can still tweak via PATCH.
 */

import { useEffect, useMemo, useState } from "react";
import { Check, Copy, FolderIcon, Link2, RotateCw, Trash2, Video as VideoIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  buildShareUrl,
  useCreateShare,
  useDeleteShare,
  useRotateShare,
  useUpdateShare,
  type CreateShareResponse,
  type ShareItemRef,
} from "@/hooks/use-share";
import { authErrorMessage } from "@/lib/auth-errors";

/**
 * Renderable metadata for one item in the dialog preview. Callers pass this in
 * so the dialog doesn't have to fetch — the item name is already known where
 * the dialog is opened from.
 */
export type ShareDialogItem = ShareItemRef & { name: string };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: ShareDialogItem[];
  /** Optional default settings — useful for entry points that want, e.g.,
   *  the watch page Share button to preselect Downloads=on. */
  defaults?: {
    downloadAllowed?: boolean;
    commentAllowed?: boolean;
    showAllVersions?: boolean;
    expiresInDays?: number;
  };
};

// Expiry options — match the memory spec's §3 range (1..3650 days). "Never"
// exists in Frame.io but Phase 1 has no hard-cap-null path server-side; we
// pin it at 10 years (server accepts up to 3650) to approximate it.
const EXPIRY_OPTIONS: Array<{ label: string; days: number }> = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "1 year", days: 365 },
  { label: "10 years", days: 3650 },
];

export function ShareDialog({ open, onOpenChange, items, defaults }: Props) {
  // Settings — owner-editable both before and after create. Post-create edits
  // go through PATCH /shares/:id, pre-create just gets attached to the POST body.
  const [name, setName] = useState<string>("");
  const [expiresInDays, setExpiresInDays] = useState<number>(defaults?.expiresInDays ?? 30);
  const [downloadAllowed, setDownloadAllowed] = useState<boolean>(defaults?.downloadAllowed ?? false);
  const [commentAllowed, setCommentAllowed] = useState<boolean>(defaults?.commentAllowed ?? true);
  const [showAllVersions, setShowAllVersions] = useState<boolean>(defaults?.showAllVersions ?? true);

  // Result state — post-create + subsequent rotate results.
  const [created, setCreated] = useState<CreateShareResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const createShare = useCreateShare();
  const updateShare = useUpdateShare();
  const deleteShare = useDeleteShare();
  const rotateShare = useRotateShare();

  // Reset form + result state on close/reopen so the dialog isn't "sticky".
  useEffect(() => {
    if (open) return;
    // Delay reset until after animation to avoid visible flicker.
    const t = setTimeout(() => {
      setCreated(null);
      setCopied(false);
      setName("");
      setExpiresInDays(defaults?.expiresInDays ?? 30);
      setDownloadAllowed(defaults?.downloadAllowed ?? false);
      setCommentAllowed(defaults?.commentAllowed ?? true);
      setShowAllVersions(defaults?.showAllVersions ?? true);
    }, 200);
    return () => clearTimeout(t);
  }, [open, defaults?.downloadAllowed, defaults?.commentAllowed, defaults?.showAllVersions, defaults?.expiresInDays]);

  const shareUrl = useMemo(
    () => (created ? buildShareUrl(created.shortToken) : ""),
    [created]
  );

  const itemPreview = useMemo(() => {
    if (items.length === 0) return "Nothing selected";
    if (items.length === 1) return items[0].name;
    return `${items.length} items`;
  }, [items]);

  async function handleCreate() {
    if (items.length === 0) return;
    const res = await createShare.mutateAsync({
      items: items.map((it) => ({ type: it.type, id: it.id })),
      name: name.trim() || null,
      expiresInDays,
      downloadAllowed,
      commentAllowed,
      showAllVersions,
    });
    setCreated(res);
  }

  async function handleCopy() {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can fail if the tab doesn't have focus / lacks permission.
      // Fall back to selecting the input so the user can Ctrl+C manually.
      const el = document.getElementById("share-url-input") as HTMLInputElement | null;
      el?.select();
    }
  }

  async function handleRotate() {
    if (!created) return;
    const res = await rotateShare.mutateAsync(created.id);
    setCreated({ ...created, shortToken: res.shortToken });
  }

  async function handleRevoke() {
    if (!created) return;
    if (!confirm("Revoke this share? The link will stop working immediately.")) return;
    await deleteShare.mutateAsync(created.id);
    onOpenChange(false);
  }

  /**
   * Post-create toggle handler — updates local state AND fires a PATCH so the
   * server row stays in sync. Owner can flip toggles as many times as they
   * want; each flip is a separate PATCH round-trip.
   */
  function makeToggleHandler(field: "downloadAllowed" | "commentAllowed" | "showAllVersions", setter: (v: boolean) => void) {
    return (v: boolean) => {
      setter(v);
      if (created) {
        updateShare.mutate({ shareId: created.id, body: { [field]: v } });
      }
    };
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{created ? "Share link ready" : "Create share link"}</DialogTitle>
          <DialogDescription>
            {created
              ? "Anyone with this link can view — settings below still control what they can do."
              : `Sharing ${itemPreview}. Anyone with the link can view.`}
          </DialogDescription>
        </DialogHeader>

        {/* Items preview — a quiet list, always shown. */}
        {items.length > 0 && (
          <div className="max-h-32 overflow-y-auto rounded-lg border border-border">
            {items.map((it) => (
              <div
                key={`${it.type}:${it.id}`}
                className="flex items-center gap-2 border-b border-border px-3 py-2 text-sm last:border-b-0"
              >
                {it.type === "folder" ? (
                  <FolderIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
                ) : (
                  <VideoIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
                )}
                <span className="truncate">{it.name}</span>
                <span className="ml-auto text-[10px] uppercase tracking-widest text-muted-foreground">
                  {it.type}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Post-create URL panel */}
        {created && (
          <div className="flex items-center gap-2 rounded-lg bg-secondary/60 px-3 py-2">
            <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
            <Input
              id="share-url-input"
              readOnly
              value={shareUrl}
              className="h-8 flex-1 border-0 bg-transparent px-0 text-sm focus-visible:ring-0"
              onFocus={(e) => e.currentTarget.select()}
            />
            <Button size="sm" variant="default" onClick={handleCopy}>
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        )}

        <Separator />

        {/* Settings — visible in both panels. Pre-create these attach to POST;
            post-create each toggle also fires a PATCH. */}
        <div className="space-y-3">
          {!created && (
            <div className="grid gap-1.5">
              <Label htmlFor="share-name" className="text-xs uppercase tracking-widest text-muted-foreground">
                Name (optional)
              </Label>
              <Input
                id="share-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={items.length === 1 ? items[0].name : `${items.length} items`}
                maxLength={200}
              />
            </div>
          )}

          {!created && (
            <div className="grid gap-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Expires in</Label>
              <div className="flex flex-wrap gap-1.5">
                {EXPIRY_OPTIONS.map((opt) => (
                  <button
                    key={opt.days}
                    type="button"
                    onClick={() => setExpiresInDays(opt.days)}
                    className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${
                      expiresInDays === opt.days
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <ToggleRow
            label="Allow downloads"
            hint="Guest can download the video file."
            checked={downloadAllowed}
            onCheckedChange={makeToggleHandler("downloadAllowed", setDownloadAllowed)}
          />
          <ToggleRow
            label="Allow comments"
            hint="Guest can leave time-anchored comments."
            checked={commentAllowed}
            onCheckedChange={makeToggleHandler("commentAllowed", setCommentAllowed)}
          />
          <ToggleRow
            label="Show all versions"
            hint="Guest can browse earlier versions in the stack."
            checked={showAllVersions}
            onCheckedChange={makeToggleHandler("showAllVersions", setShowAllVersions)}
          />
        </div>

        {/* Error surface */}
        {createShare.isError && !created && (
          <p className="text-xs text-destructive">{authErrorMessage(createShare.error)}</p>
        )}
        {updateShare.isError && (
          <p className="text-xs text-destructive">{authErrorMessage(updateShare.error)}</p>
        )}
        {rotateShare.isError && (
          <p className="text-xs text-destructive">{authErrorMessage(rotateShare.error)}</p>
        )}
        {deleteShare.isError && (
          <p className="text-xs text-destructive">{authErrorMessage(deleteShare.error)}</p>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between gap-2 pt-2">
          {created ? (
            <>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRotate}
                  disabled={rotateShare.isPending}
                >
                  <RotateCw className="h-3.5 w-3.5" />
                  {rotateShare.isPending ? "Rotating…" : "Rotate link"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRevoke}
                  disabled={deleteShare.isPending}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {deleteShare.isPending ? "Revoking…" : "Revoke"}
                </Button>
              </div>
              <Button variant="default" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleCreate}
                disabled={items.length === 0 || createShare.isPending}
              >
                {createShare.isPending ? "Creating…" : "Create link"}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ToggleRow({
  label,
  hint,
  checked,
  onCheckedChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-foreground">{label}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} className="mt-0.5" />
    </div>
  );
}
